import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Utils, getSubscriptionDisplayStatus, formatDateTime, formatTime, formatRelativeDate } from '../../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FiCalendar, FiChevronDown, FiSmartphone } from "react-icons/fi";
import { MdDeleteOutline, MdEdit, MdClose, MdCheck } from "react-icons/md";
import { FaAndroid } from "react-icons/fa";
import Swal from "sweetalert2";
import './UserProfile.css';

const UserProfile = () => {
    const { userId } = useParams();
    const [userData, setUserData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDays, setSelectedDays] = useState(3);
    const [showTagModal, setShowTagModal] = useState(false);
    const [tagKey, setTagKey] = useState('');
    const [tagValue, setTagValue] = useState('');
    const [isSavingTag, setIsSavingTag] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingTagKey, setEditingTagKey] = useState('');
    const [openDropdown, setOpenDropdown] = useState(null);
    const [activityData, setActivityData] = useState([]);
    const [isLoadingActivity, setIsLoadingActivity] = useState(false);
    const [expandedActivity, setExpandedActivity] = useState(null);

    useEffect(() => {
        fetchUserProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    useEffect(() => {
        if (userData?.notify_sphere_id) {
            fetchActivityTimeline();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, selectedDays]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdown && !event.target.closest('.tag-menu-wrapper')) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [openDropdown]);

    const fetchUserProfile = async () => {
        try {
            const response = await fetch(Utils.baseurl + 'get/user/profile', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id: userId }),
            });
            const result = await response.json();
            if (result.status) {
                setUserData(result.data);
            }
        } catch (error) {
            console.error('Error fetching user profile:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchActivityTimeline = async () => {
        if (!userData?.notify_sphere_id) return;

        setIsLoadingActivity(true);
        try {
            const response = await fetch(Utils.baseurl + 'get/user/activity', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ notify_sphere_id: userData.notify_sphere_id, days: selectedDays }),
            });
            const result = await response.json();
            if (result.status) {
                setActivityData(result.data || []);
            }
        } catch (error) {
            console.error('Error fetching activity timeline:', error);
        } finally {
            setIsLoadingActivity(false);
        }
    };

    if (isLoading) {
        return (
            <div className="dashboard_right">
                <div className="dashboard_inner">
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    </div>
                </div>
            </div>
        );
    }

    if (!userData) {
        return (
            <div className="dashboard_right">
                <div className="dashboard_inner">
                    <p>User not found</p>
                </div>
            </div>
        );
    }

    const formatHourAmPm = (hour) => {
        const d = new Date();
        d.setHours(hour, 0, 0, 0);
        return formatTime(d);
    };

    const formatBestHour = (hourValue) => {
        const hour = Number(hourValue);
        if (!Number.isFinite(hour) || hour < 0 || hour > 23) return 'N/A';
        const start = formatHourAmPm(hour);
        const end = formatHourAmPm((hour + 1) % 24);
        return start && end ? `${start} - ${end}` : 'N/A';
    };

    const getDeviceIcon = (deviceModel) => {
        const model = (deviceModel || '').toLowerCase();
        const commonStyle = { width: '16px', height: '16px', verticalAlign: 'middle' };

        if (model.includes('android')) {
            return <FaAndroid style={commonStyle} aria-label="Android device" title="Android device" />;
        }

        return <FiSmartphone style={commonStyle} aria-label="Mobile device" title="Mobile device" />;
    };

    const subscriptions = userData.subscriptions || [];

    const handleAddTag = () => {
        setIsEditMode(false);
        setEditingTagKey('');
        setShowTagModal(true);
        setTagKey('');
        setTagValue('');
    };

    const handleEditTag = (key, value) => {
        setIsEditMode(true);
        setEditingTagKey(key);
        setTagKey(key);
        setTagValue(value);
        setShowTagModal(true);
        setOpenDropdown(null);
    };

    const handleCloseModal = () => {
        setShowTagModal(false);
        setTagKey('');
        setTagValue('');
        setIsEditMode(false);
        setEditingTagKey('');
    };

    const handleSaveTag = async () => {
        if (!tagKey.trim() || !tagValue.trim()) {
            toast.warning('Please enter both key and value');
            return;
        }

        setIsSavingTag(true);
        try {
            const endpoint = isEditMode ? 'update/user/tag' : 'add/user/tag';
            const body = isEditMode
                ? { userId: userId, oldKey: editingTagKey, key: tagKey.trim(), value: tagValue.trim() }
                : { userId: userId, key: tagKey.trim(), value: tagValue.trim() };

            const response = await fetch(Utils.baseurl + endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });
            const result = await response.json();
            if (result.status) {
                await fetchUserProfile();
                handleCloseModal();
            } else {
                toast.error(result.message || `Failed to ${isEditMode ? 'update' : 'add'} tag`);
            }
        } catch (error) {
            console.error(`Error ${isEditMode ? 'updating' : 'adding'} tag:`, error);
            toast.error(`Error ${isEditMode ? 'updating' : 'adding'} tag`);
        } finally {
            setIsSavingTag(false);
        }
    };

    const handleRemoveTag = async (key) => {
        const confirm = await Swal.fire({
            title: "Are you sure?",
            text: `Are you sure you want to remove the tag "${key}"?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, remove",
        });
        if (!confirm.isConfirmed) return;

        try {
            const response = await fetch(Utils.baseurl + 'remove/user/tag', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId: userId,
                    key: key
                }),
            });
            const result = await response.json();
            if (result.status) {
                await fetchUserProfile();
                setOpenDropdown(null);
            } else {
                toast.error(result.message || 'Failed to remove tag');
            }
        } catch (error) {
            console.error('Error removing tag:', error);
            toast.error('Error removing tag');
        }
    };

    const toggleDropdown = (key) => {
        setOpenDropdown(openDropdown === key ? null : key);
    };

    const toggleActivityExpand = (activityId) => {
        setExpandedActivity(expandedActivity === activityId ? null : activityId);
    };

    const formatActivityDate = (dateString) => {
        if (!dateString) return 'N/A';
        return formatDateTime(dateString, { seconds: true }) || 'N/A';
    };

    const getActivityStatus = (activity) => {
        if (activity.onPressStatus === 1) {
            return 'Push Notification Clicked';
        } else if (activity.sentStatus === 1) {
            return 'Push Notification Received';
        } else {
            return 'Push Notification Sent';
        }
    };

    return (
        <div className="dashboard_right">
            <div className="dashboard_inner">
                <div className="user-profile-header">
                    <h1>notifySphere ID: {userData.notify_sphere_id}</h1>
                    <button className="actions-button">
                        Actions
                        <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '12px', height: '12px', marginLeft: '5px' }}>
                            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" />
                        </svg>
                    </button>
                </div>

                <div className="user-profile-content">
                    {/* Left Column - Profile */}
                    <div className="profile-section">
                        <h3>Profile</h3>
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 14.5A6.5 6.5 0 118 1.5a6.5 6.5 0 010 13z" />
                            </svg>
                            <span>Country: {userData.country || 'N/A'}</span>
                        </div>
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M8 2a6 6 0 100 12A6 6 0 008 2zm0 11a5 5 0 110-10 5 5 0 010 10zm1-8H7v3h3V7H9V5z" />
                            </svg>
                            <span>State: {userData.state || 'N/A'}</span>
                        </div>
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm3.5 7.5a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0z" />
                            </svg>
                            <span>City: {userData.city || 'N/A'}</span>
                        </div>
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M2 3h12v2H2V3zm0 4h12v2H2V7zm0 4h12v2H2v-2z" />
                            </svg>
                            <span>Language: {userData.language || 'N/A'}</span>
                        </div>
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm1 12H7V7h2v5zm0-6H7V4h2v2z" />
                            </svg>
                            <span>Time Zone: {userData.timezone || 'N/A'}</span>
                        </div>
                        {userData.email && (
                            <div className="profile-item">
                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                    <path d="M2 4h12v8H2V4zm1 1l5 3 5-3v6H3V5z" />
                                </svg>
                                <span>Email: {userData.email}</span>
                            </div>
                        )}
                        {userData.phone && (
                            <div className="profile-item">
                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                    <path d="M3 2h10v12H3V2zm2 2v8h6V4H5z" />
                                </svg>
                                <span>Phone: {userData.phone}</span>
                            </div>
                        )}
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm1 12H7V7h2v5z" />
                            </svg>
                            <span>Last Session: {formatRelativeDate(userData.last_session) || 'N/A'}</span>
                        </div>
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm1 12H7V7h2v5z" />
                            </svg>
                            <span>First Session: {formatRelativeDate(userData.first_session) || 'N/A'}</span>
                        </div>
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M2 8a6 6 0 1112 0A6 6 0 012 8zm6-4a4 4 0 100 8 4 4 0 000-8z" />
                            </svg>
                            <span>Session Count: {Number(userData.session_count || 0)}</span>
                        </div>
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 3a.75.75 0 01.75.75v4.19l2.28 2.28a.75.75 0 11-1.06 1.06L7.47 8.78A.75.75 0 017.25 8.25V3.75A.75.75 0 018 3z" />
                            </svg>
                            <span>Best Active Time: {formatBestHour(userData.best_send_hour_local)}</span>
                        </div>
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M2 3h12v10H2V3zm1 1v8h10V4H3zm2 2h2v4H5V6zm4 1h2v3H9V7z" />
                            </svg>
                            <span>Activity Score: {Number(userData.best_send_hour_score || 0)}</span>
                        </div>
                        <div className="profile-item">
                            <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                <path d="M8 1a7 7 0 107 7A7 7 0 008 1zm0 1.5a5.5 5.5 0 11-5.5 5.5A5.5 5.5 0 018 2.5zM7.25 4h1.5v4.25H7.25zm0 5.5h1.5V11h-1.5z" />
                            </svg>
                            <span>Activity Last Tracked: {formatRelativeDate(userData.activity_last_tracked_at) || 'N/A'}</span>
                        </div>

                        {/* Subscriptions Section */}
                        <div className="subscriptions-section">
                            <h3>Subscriptions ({subscriptions.length})</h3>
                            {subscriptions.length > 0 ? (
                                subscriptions.map((subscription, index) => {
                                    const subStatusUi = getSubscriptionDisplayStatus(subscription.status);
                                    return (
                                        <div key={subscription.id || index} className="subscription-item">
                                            <div className="subscription-header">
                                                <div>
                                                    <span style={{ marginRight: '8px' }}>
                                                        {getDeviceIcon(subscription.device_model)}
                                                    </span>
                                                    <span>
                                                        {subscription.device_model || 'Unknown Device'}
                                                        {subscription.device_os && ` (${subscription.device_os}${subscription.device_version ? ` ${subscription.device_version}` : ''})`}
                                                    </span>
                                                </div>
                                                {subStatusUi.badgeClass ? (
                                                    <span className={subStatusUi.badgeClass}>{subStatusUi.label}</span>
                                                ) : (
                                                    <span>{subStatusUi.label}</span>
                                                )}
                                            </div>
                                            {subscription.app_version && (
                                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                    App Version: {subscription.app_version}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <p style={{ color: '#666', fontSize: '14px' }}>No subscriptions found</p>
                            )}
                        </div>

                        {/* IDs Section */}
                        <div className="ids-section">
                            <h3>IDs</h3>
                            <div className="id-item">
                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                                </svg>
                                <span>notifySphere ID: {userData.notify_sphere_id}</span>
                                <button className="copy-button" onClick={() => navigator.clipboard.writeText(userData.notify_sphere_id)}>
                                    <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '14px', height: '14px' }}>
                                        <path d="M4 2h8v2H4V2zm0 4h8v2H4V6zm0 4h5v2H4v-2z" />
                                    </svg>
                                </button>
                            </div>
                            {subscriptions.length > 0 && (
                                <>
                                    {subscriptions.map((subscription, index) => (
                                        subscription.subscription_id && (
                                            <div key={`sub-${subscription.id || index}`} className="id-item">
                                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                                                </svg>
                                                <span>Subscription ID {subscriptions.length > 1 ? `${index + 1}` : ''}: {subscription.subscription_id}</span>
                                                <button className="copy-button" onClick={() => navigator.clipboard.writeText(subscription.subscription_id)}>
                                                    <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '14px', height: '14px' }}>
                                                        <path d="M4 2h8v2H4V2zm0 4h8v2H4V6zm0 4h5v2H4v-2z" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )
                                    ))}
                                </>
                            )}
                            <div className="id-item">
                                <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0z" />
                                </svg>
                                <span>External ID:</span>
                                <button className="add-button">
                                    <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '14px', height: '14px' }}>
                                        <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" fill="none" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* User Tags Section */}
                        <div className="tags-section">
                            <h3>User Tags ({userData.tags ? Object.keys(JSON.parse(userData.tags)).length : 0})</h3>
                            <button className="add-tag-button" onClick={handleAddTag}>+ Add Tag</button>
                            <div className="tags-list">
                                {userData.tags && (() => {
                                    try {
                                        const tagsObj = typeof userData.tags === 'string' ? JSON.parse(userData.tags) : userData.tags;
                                        return Object.entries(tagsObj).map(([key, value], idx) => (
                                            <div key={idx} className="tag-item">
                                                <div className="tag-item-content">
                                                    <span className="tag-item-key">{key}</span>
                                                    <span className="tag-item-value">{value}</span>
                                                </div>
                                                <div className="tag-menu-wrapper">
                                                    <button
                                                        className="tag-menu-button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleDropdown(key);
                                                        }}
                                                        aria-label="Tag options"
                                                    >
                                                        <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                                                            <circle cx="8" cy="3" r="1.5" />
                                                            <circle cx="8" cy="8" r="1.5" />
                                                            <circle cx="8" cy="13" r="1.5" />
                                                        </svg>
                                                    </button>
                                                    {openDropdown === key && (
                                                        <div className="tag-dropdown">
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={() => handleEditTag(key, value)}
                                                            >
                                                                <MdEdit size={18} aria-hidden="true" />
                                                                Edit
                                                            </button>
                                                            <button
                                                                className="dropdown-item text-danger"
                                                                onClick={() => handleRemoveTag(key)}
                                                            >
                                                                <MdDeleteOutline size={18} aria-hidden="true" />
                                                                Remove
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ));
                                    } catch (e) {
                                        return null;
                                    }
                                })()}
                            </div>
                        </div>

                        {/* Automations Audience Section */}
                        <div className="automations-section">
                            <h3>Automations Audience (0)</h3>
                            <p className="automations-link">Read our Automations documentation to learn how to use Automations.</p>
                        </div>
                    </div>

                    {/* Right Column - Activity Timeline */}
                    <div className="activity-section">
                        <h3>Activity Timeline</h3>
                        <p className="activity-subtitle">Tracked up to {selectedDays} days</p>
                        <div className="days-selector">
                            <button className={selectedDays === 3 ? 'active' : ''} onClick={() => setSelectedDays(3)}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <FiCalendar size={16} />
                                    <span>3 days</span>
                                    <FiChevronDown size={16} />
                                </span>
                            </button>
                        </div>

                        {isLoadingActivity ? (
                            <div style={{ textAlign: 'center', padding: '30px' }}>
                                <FontAwesomeIcon icon={faSpinner} spin size="lg" />
                            </div>
                        ) : activityData.length > 0 ? (
                            <div className="activity-timeline">
                                {activityData.map((activity) => (
                                    <div key={activity.id} className="activity-item">
                                        <div
                                            className="activity-item-header"
                                            onClick={() => toggleActivityExpand(activity.id)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <svg
                                                viewBox="0 0 16 16"
                                                fill="currentColor"
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    transform: expandedActivity === activity.id ? 'rotate(90deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.2s'
                                                }}
                                            >
                                                <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" fill="none" />
                                            </svg>
                                            <span className="activity-timestamp">{formatActivityDate(activity.timestamp)}</span>
                                            <span className="activity-status">{getActivityStatus(activity)}</span>
                                        </div>

                                        {expandedActivity === activity.id && activity.notification && (
                                            <div className="activity-details">
                                                <h4>Message Details</h4>
                                                <div className="activity-detail-row">
                                                    <span className="detail-label">Name</span>
                                                    <span className="detail-value">{activity.notification_id || 'N/A'}</span>
                                                </div>
                                                <div className="activity-detail-row">
                                                    <span className="detail-label">Sent</span>
                                                    <span className="detail-value">{formatActivityDate(activity.notification.sent_at)}</span>
                                                </div>
                                                <div className="activity-detail-row">
                                                    <span className="detail-label">Message Title</span>
                                                    <span className="detail-value">{activity.notification.title || 'N/A'}</span>
                                                </div>
                                                <div className="activity-detail-row">
                                                    <span className="detail-label">Message Content</span>
                                                    <span className="detail-value">{activity.notification.message || 'N/A'}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-activity">
                                <div className="no-activity-icon">
                                    <svg viewBox="0 0 100 100" fill="none" style={{ width: '80px', height: '80px' }}>
                                        <circle cx="50" cy="50" r="40" fill="#e8eaed" />
                                        <circle cx="50" cy="40" r="15" fill="#fff" />
                                        <path d="M30 70c0-11 9-20 20-20s20 9 20 20" fill="#fff" />
                                    </svg>
                                </div>
                                <h4>No Recent Activity</h4>
                                <p>You'll see data here when there's user activity.</p>
                            </div>
                        )}
                    </div>
                </div>

            </div>

            {/* Add Tag Modal - Rendered using Portal with Bootstrap */}
            {showTagModal && ReactDOM.createPortal(
                <div className="modal fade show d-block ns-modal" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }} onClick={handleCloseModal}>
                    <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header border-bottom">
                                <h5 className="modal-title">{isEditMode ? 'Edit Tag' : 'Add Tag'}</h5>
                                <button type="button" className="btn-close" onClick={handleCloseModal} aria-label="Close"></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label htmlFor="tag-key" className="form-label">Key</label>
                                    <input
                                        id="tag-key"
                                        type="text"
                                        value={tagKey}
                                        onChange={(e) => setTagKey(e.target.value)}
                                        className="form-control"
                                        autoFocus
                                    />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="tag-value" className="form-label">Value</label>
                                    <input
                                        id="tag-value"
                                        type="text"
                                        value={tagValue}
                                        onChange={(e) => setTagValue(e.target.value)}
                                        className="form-control"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={handleCloseModal}>
                                    <MdClose />
                                    <span>Cancel</span>
                                </button>
                                <button
                                    type="button"
                                    className="add_app_btn btn_theme_primary"
                                    onClick={handleSaveTag}
                                    disabled={isSavingTag}
                                >
                                    <MdCheck />
                                    <span>{isSavingTag ? 'Saving...' : 'Save'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default UserProfile;
